import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Hammer, Sparkles } from "lucide-react";

// Patch 68 — the Split-View Canvas is the one post-login landing for EVERY user. If the user already
// has a project we send them straight to its Split-View Canvas; if they have none (incl. brand-new
// sign-ups) we show the canvas's empty "start here" state so they never land on the Workspace list.
export default function BuildHome() {
  const { org } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("loading"); // loading | empty
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!org?.id) return;
    api.get(`/orgs/${org.id}/projects`).then((r) => {
      const list = (r.data || []).filter((p) => !p.pending_delete && !p.archived);
      const latest = [...list].sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))[0];
      if (latest) nav(`/workspace/project/${latest.id}`, { replace: true });
      else setState("empty");
    }).catch(() => setState("empty"));
  }, [org, nav]);

  const createFirst = async () => {
    if (!org?.id) return;
    setBusy(true);
    try {
      const { data } = await api.post("/projects", { name: name.trim() || "My first app", description: desc, org_id: org.id });
      nav(`/workspace/project/${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  if (state === "loading") {
    return <Shell active="workspace"><div data-testid="build-home-loading" className="k-muted" style={{ padding: 40 }}>Loading your Split-View Canvas…</div></Shell>;
  }

  return (
    <Shell active="workspace">
      <div data-testid="split-view-empty" style={{ maxWidth: 760, margin: "0 auto", paddingTop: 12 }}>
        <div className="k-container k-accent-card" style={{ padding: 28 }}>
          <div className="k-heading" style={{ fontSize: 26, fontWeight: 800, display: "flex", alignItems: "center", gap: 10 }}><Sparkles size={22} /> Start here</div>
          <p className="k-muted" style={{ marginTop: 8, fontSize: 14, lineHeight: 1.5 }}>
            This is your Split-View Canvas, where every build happens. Describe your first app in plain
            English and Kadana builds it as compliance-ready software across web, iOS and Android.
          </p>
          <input className="k-control" data-testid="start-name" style={{ marginTop: 18 }}
            placeholder="App name (e.g. Invoicing for my shop)" value={name} onChange={(e) => setName(e.target.value)} />
          <textarea className="k-control" data-testid="start-desc" rows={4} style={{ marginTop: 10 }}
            placeholder="Describe what you want in plain English…" value={desc} onChange={(e) => setDesc(e.target.value)} />
          <button className="k-btn k-btn-primary" data-testid="start-create" style={{ marginTop: 14 }}
            disabled={busy || !name.trim()} onClick={createFirst}><Hammer size={15} /> Create my first app</button>
          <p className="k-muted" style={{ fontSize: 12, marginTop: 12 }}>Your saved apps are always reachable from the Workspace and Apps menus.</p>
        </div>
      </div>
    </Shell>
  );
}
